import { Combobox, Group, Pagination, Text, useCombobox } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import type { ComponentProps } from 'react';

export type PaginationFooterProps = ComponentProps<typeof Pagination> & {
  currentPage: number;
  pageLimit: number;
  setCurrentPage: (page: number) => void;
  setPageLimit: (limit: number) => void;
};

export function PaginationFooter({
  currentPage,
  pageLimit,
  setCurrentPage,
  setPageLimit,
  total,
}: PaginationFooterProps) {
  const { t } = useTranslation();

  const firstItem = (currentPage - 1) * pageLimit + 1;
  const lastItem = Math.min(currentPage * pageLimit, total);
  const pageCount = Math.ceil(total / pageLimit);

  return (
    <Group
      justify="space-between"
      p="sm"
      style={theme => ({
        borderTop: `1px solid ${theme.colors.gray[2]}`,
      })}
    >
      <Group>
        {total > 0 && (
          <Text size="sm">
            {firstItem} - {lastItem} {t(i18nKeys.OF)} {total}
          </Text>
        )}
        <ItemsPerPageSelector
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          setCurrentPage={setCurrentPage}
        />
      </Group>
      <Pagination size="sm" value={currentPage} onChange={setCurrentPage} total={pageCount} />
    </Group>
  );
}

type ItemsPerPageSelectorProps = {
  pageLimit: number;
  setPageLimit: (limit: number) => void;
  setCurrentPage: (page: number) => void;
};

const ItemsPerPageSelector = ({
  pageLimit,
  setPageLimit,
  setCurrentPage,
}: ItemsPerPageSelectorProps) => {
  const { t } = useTranslation();
  const combobox = useCombobox();

  return (
    <Combobox
      store={combobox}
      width={250}
      position="bottom-start"
      withArrow
      onOptionSubmit={val => {
        setPageLimit(Number(val));
        setCurrentPage(1);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <Group onClick={() => combobox.toggleDropdown()} style={{ cursor: 'pointer' }} gap={4}>
          <Text size="sm">
            {pageLimit} {t(i18nKeys.PER_PAGE)}
          </Text>
          <IconChevronDown stroke={1.5} size={16} />
        </Group>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>{PAGE_LIMIT_OPTIONS}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

const PAGE_LIMIT_OPTIONS = [10, 20, 50, 100].map(item => (
  <Combobox.Option value={item.toString()} key={item}>
    {item}
  </Combobox.Option>
));
